import { useEffect, useRef } from 'react'
import { hashKey, keepPreviousData, QueryKey, useQuery, UseQueryResult } from '@tanstack/react-query'

export interface SearchQueryOptions<TData> {
    /** Every server owned value (criteria, filters, pagination) must be part of the key. */
    queryKey: QueryKey
    queryFn: () => Promise<TData>
    /** Results that are already available (e.g. restored from a store) for the first key only. */
    initialData?: TData
    enabled?: boolean
    onError?: (error: Error) => void
    /**
     * Copies the results for the key being asked for into the page's store, which is what most of the portal's
     * screens read. Required, because the fetch is the wrong place to do it: on a cache hit the fetch never runs
     * (the first key is treated as fresh), so a store that was cleared in the meantime — a layout resetting on
     * unmount, or a link's search terms re-seeding it — would stay empty while the cache holds the page.
     *
     * Runs whenever the data for the current key arrives or is refetched, never for the previous page held as a
     * placeholder, and not for `initialData` (the store already has it). Pass `'none'` only for a page that renders
     * the query's own `data` and keeps nothing in a store.
     */
    applyData: ((data: TData) => void) | 'none'
}

/**
 * Shared TanStack Query wiring for the portal's paginated searches.
 *
 * Searching is an explicit user action, so a request is only made when the query key changes.
 * The previous page is kept while the next one loads so the results section is not torn down.
 */
export const useSearchQuery = <TData>({
    queryKey,
    queryFn,
    initialData,
    enabled = true,
    onError,
    applyData,
}: SearchQueryOptions<TData>): UseQueryResult<TData, Error> => {
    const initialQueryHash = useRef(hashKey(queryKey))
    const hasSearchedRef = useRef(false)

    if (initialQueryHash.current !== hashKey(queryKey)) {
        hasSearchedRef.current = true
    }
    // Seeding only applies until the user runs their first search; from then on every key change
    // hits the server, so a cached page is refreshed in the background instead of served as is.
    const isSeeded = !hasSearchedRef.current

    const result = useQuery<TData, Error>({
        queryKey,
        queryFn: async () => {
            try {
                return await queryFn()
            } catch (error) {
                onError?.(error as Error)
                throw error
            }
        },
        initialData: isSeeded ? initialData : undefined,
        staleTime: isSeeded ? Number.POSITIVE_INFINITY : 0,
        placeholderData: keepPreviousData,
        enabled,
    })

    const { data, dataUpdatedAt, isPlaceholderData } = result
    const queryHash = hashKey(queryKey)
    useEffect(() => {
        if (typeof applyData !== 'function' || data === undefined || isPlaceholderData || data === initialData) {
            return
        }
        try {
            applyData(data)
        } catch (error) {
            // Was a fetch error (reported, page intact) when this ran inside the fetch; an exception thrown from an
            // effect would take the whole page down instead.
            onError?.(error as Error)
        }
        // `dataUpdatedAt` is a dependency because a refetch that returns identical data hands back the very same
        // object, after the store may have been cleared for the new submit. `queryHash` because the callback closes
        // over the render's key (page, filters), which must be the one the data belongs to.
    }, [data, dataUpdatedAt, isPlaceholderData, queryHash])

    return result
}

export default useSearchQuery
